import '@/App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/layout/Layout';
import { useLocation } from 'react-router-dom';

// Apply persisted dark mode before first paint
if (localStorage.getItem('da_dark_mode') === '1') {
  document.documentElement.classList.add('dark');
}

// ── Shared page-load fallback ─────────────────────────────────────────────────
const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-purple-200 border-t-[#5B21B6] rounded-full animate-spin mx-auto mb-2" />
      <p className="text-xs text-gray-400">Loading…</p>
    </div>
  </div>
);

// ── Public / shared pages ─────────────────────────────────────────────────────
// Landing stays eager — it is the cold-start entry point, zero extra cost
import Landing from '@/pages/Landing';
// Auth pages are tiny and needed immediately after cold start
import Login from '@/pages/Login';

// Everything else is lazy — each becomes its own JS chunk
const Register           = lazy(() => import('@/pages/Register'));
const OfferSharePage     = lazy(() => import('@/pages/OfferSharePage'));
const AuthCallback       = lazy(() => import('@/pages/AuthCallback'));
const QRRestaurantExp    = lazy(() => import('@/pages/qr/QRRestaurantExperience'));

// ── Diner pages ───────────────────────────────────────────────────────────────
const Discover           = lazy(() => import('@/pages/diner/Discover'));
const DinerOffers        = lazy(() => import('@/pages/diner/Offers'));
const DinerOfferDetail   = lazy(() => import('@/pages/diner/OfferDetail'));
const QRScan             = lazy(() => import('@/pages/diner/QRScan'));
const Saved              = lazy(() => import('@/pages/diner/Saved'));
const DinerProfile       = lazy(() => import('@/pages/diner/Profile'));
const RestaurantDetail   = lazy(() => import('@/pages/diner/RestaurantDetail'));
const DinerFeedback      = lazy(() => import('@/pages/diner/DinerFeedback'));
const DinerCoupons       = lazy(() => import('@/pages/diner/DinerCoupons'));
const DinerChat          = lazy(() => import('@/pages/diner/DinerChat'));

// ── Vendor pages ──────────────────────────────────────────────────────────────
const VendorDashboard    = lazy(() => import('@/pages/vendor/Dashboard'));
const VendorOffers       = lazy(() => import('@/pages/vendor/VendorOffers'));
const MenuManagement     = lazy(() => import('@/pages/vendor/MenuManagement'));
// Analytics — recharts is a heavy library; lazy-loading this route keeps recharts
// out of the main bundle entirely. It is only fetched when /vendor/analytics is visited.
const Analytics          = lazy(() => import('@/pages/vendor/Analytics'));
const Wallet             = lazy(() => import('@/pages/vendor/Wallet'));
const VendorProfile      = lazy(() => import('@/pages/vendor/VendorProfile'));
const VendorFeedback     = lazy(() => import('@/pages/vendor/VendorFeedback'));
// POS — the heaviest vendor module; isolated in its own chunk from day one
const VendorPOS          = lazy(() => import('@/pages/vendor/POS'));

// ── Admin pages (recharts used in AdminDashboard; lazy-loaded here) ───────────
// AdminDashboard imports recharts — kept in its own chunk, not the main bundle.
const AdminDashboard     = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers         = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminRestaurants   = lazy(() => import('@/pages/admin/AdminRestaurants'));
const AdminCMS           = lazy(() => import('@/pages/admin/AdminCMS'));
const AdminReviews       = lazy(() => import('@/pages/admin/AdminReviews'));

// ── Route tree ────────────────────────────────────────────────────────────────
function AppRoutes() {
  const location = useLocation();
  // Google OAuth redirect lands here with #session_id=... in the URL hash
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/"            element={<Landing />} />
      <Route path="/login"       element={<Login role="diner" />} />
      <Route path="/vendor/login" element={<Login role="vendor" />} />
      <Route path="/admin/login"  element={<Login role="admin" />} />

      <Route path="/register" element={
        <Suspense fallback={<PageFallback />}><Register /></Suspense>
      } />
      <Route path="/offer/:id" element={
        <Suspense fallback={<PageFallback />}><OfferSharePage /></Suspense>
      } />

      {/* QR Restaurant Experience — public, no auth, no layout */}
      <Route path="/qr/:token" element={
        <Suspense fallback={<PageFallback />}><QRRestaurantExp /></Suspense>
      } />

      {/* ── Diner ── */}
      <Route path="/diner/*" element={
        <ProtectedRoute role="diner">
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route index element={<Navigate to="discover" replace />} />
                  <Route path="discover"         element={<Discover />} />
                  <Route path="offers"           element={<DinerOffers />} />
                  <Route path="offers/:id"       element={<DinerOfferDetail />} />
                  <Route path="scan"             element={<QRScan />} />
                  <Route path="saved"            element={<Saved />} />
                  <Route path="profile"          element={<DinerProfile />} />
                  <Route path="profile/reviews"  element={<DinerFeedback />} />
                  <Route path="profile/coupons"  element={<DinerCoupons />} />
                  <Route path="chat/:convId"     element={<DinerChat />} />
                  <Route path="restaurant/:id"   element={<RestaurantDetail />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      {/* ── Vendor ── */}
      <Route path="/vendor/*" element={
        <ProtectedRoute role="vendor">
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard"  element={<VendorDashboard />} />
                  <Route path="offers"     element={<VendorOffers />} />
                  <Route path="menu"       element={<MenuManagement />} />
                  <Route path="analytics"  element={<Analytics />} />
                  <Route path="feedback"   element={<VendorFeedback />} />
                  <Route path="wallet"     element={<Wallet />} />
                  <Route path="profile"    element={<VendorProfile />} />
                  <Route path="pos"        element={<VendorPOS />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      {/* ── Admin ── */}
      <Route path="/admin/*" element={
        <ProtectedRoute role="admin">
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route index         element={<AdminDashboard />} />
                  <Route path="users"       element={<AdminUsers />} />
                  <Route path="restaurants" element={<AdminRestaurants />} />
                  <Route path="reviews"     element={<AdminReviews />} />
                  <Route path="cms"         element={<AdminCMS />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
